import { FadeUp } from "./Reveal";
import { useLang } from "./LanguageContext";

const copy = {
  en: {
    kicker: "Business Models",
    h2: <>Two ways to <em className="italic">bring it to life.</em></>,
    models: [
      { n: "01", title: "Event & Wedding", tag: "Live Culinary Performance", body: "A live mozzarella performance as the centerpiece of weddings and private events — artisan theatre, aroma and craft unfolding wherever guests gather." },
      { n: "02", title: "Full Concept Restaurant", tag: "Highest Ceiling", body: "The complete build: the live Mozzarella Show at the heart of a standalone venue or brand extension — the strongest long-term upside." },
    ],
  },
  it: {
    kicker: "Modelli di Business",
    h2: <>Due modi per <em className="italic">darle vita.</em></>,
    models: [
      { n: "01", title: "Eventi & Matrimoni", tag: "Live Culinary Performance", body: "Una performance di mozzarella dal vivo come cuore di matrimoni ed eventi privati — teatro artigiano, profumo e mestiere che prende vita ovunque si riuniscano gli ospiti." },
      { n: "02", title: "Ristorante Full Concept", tag: "Massimo Potenziale", body: "Il progetto completo: il Mozzarella Show dal vivo al centro di un locale indipendente o di un'estensione del brand — il potenziale più forte nel lungo periodo." },
    ],
  },
  ru: {
    kicker: "Бизнес-модели",
    h2: <>Два способа <em className="italic">воплотить это в жизнь.</em></>,
    models: [
      { n: "01", title: "События и свадьбы", tag: "Живое кулинарное шоу", body: "Живая performance моцареллы как сердце свадеб и частных событий — ремесленный театр и аромат, где бы ни собрались гости." },
      { n: "02", title: "Ресторан Full Concept", tag: "Максимальный потенциал", body: "Полный проект: живое Mozzarella Show в сердце самостоятельного заведения или расширения бренда." },
    ],
  },
  es: {
    kicker: "Modelos de Negocio",
    h2: <>Dos formas de <em className="italic">darle vida.</em></>,
    models: [
      { n: "01", title: "Eventos & Bodas", tag: "Live Culinary Performance", body: "Una performance de mozzarella en vivo como corazón de bodas y eventos privados — teatro artesano, aroma y oficio que cobra vida dondequiera que se reúnan los invitados." },
      { n: "02", title: "Restaurante Full Concept", tag: "Máximo Potencial", body: "El proyecto completo: el Mozzarella Show en vivo en el corazón de un local independiente o extensión de marca — el mayor potencial a largo plazo." },
    ],
  },
};

const luxCopy = {
  en: {
    kicker: "Business Models",
    h2: <>Two ways to <em className="italic">bring it to life.</em></>,
    models: [
      { n: "01", title: "Luxury Weddings", tag: "Signature Add-On", body: "A live mozzarella atelier as the centerpiece of the reception — Italian maestri, theatre and aroma, an élite entertainment moment your guests have never seen." },
      { n: "02", title: "Grand Events & Gala", tag: "The Pentolone Show", body: "For galas, grand receptions and five-star venues: the giant pot staged live in front of hundreds of guests — spectacular, scalable, unforgettable." },
    ],
  },
  it: {
    kicker: "Modelli di Business",
    h2: <>Due modi per <em className="italic">darle vita.</em></>,
    models: [
      { n: "01", title: "Matrimoni Luxury", tag: "Add-On Signature", body: "Un atelier di mozzarella dal vivo come cuore del ricevimento — maestri italiani, teatro e profumo, un momento d'intrattenimento d'élite che i tuoi ospiti non hanno mai visto." },
      { n: "02", title: "Grandi Eventi & Gala", tag: "Il Pentolone Show", body: "Per gala, banchetti e strutture a cinque stelle: il pentolone in scena dal vivo davanti a centinaia di ospiti — spettacolare, scalabile, indimenticabile." },
    ],
  },
  ru: {
    kicker: "Бизнес-модели",
    h2: <>Два способа <em className="italic">воплотить это в жизнь.</em></>,
    models: [
      { n: "01", title: "Свадьбы люкс", tag: "Фирменный аддон", body: "Живое ателье моцареллы как сердце приёма — итальянские мастера, театр и аромат, элитный момент, которого ваши гости ещё не видели." },
      { n: "02", title: "Большие события и гала", tag: "Шоу Пентолоне", body: "Для гала-ужинов, банкетов и пятизвёздочных отелей: гигантский котёл вживую перед сотнями гостей — зрелищно, масштабируемо, незабываемо." },
    ],
  },
  es: {
    kicker: "Modelos de Negocio",
    h2: <>Dos formas de <em className="italic">darle vida.</em></>,
    models: [
      { n: "01", title: "Bodas de Lujo", tag: "Add-On Signature", body: "Un atelier de mozzarella en vivo como corazón de la recepción — maestros italianos, teatro y aroma, un momento de entretenimiento de élite que tus invitados nunca han visto." },
      { n: "02", title: "Grandes Eventos & Gala", tag: "El Pentolone Show", body: "Para galas, grandes recepciones y hoteles cinco estrellas: la olla gigante en escena, en vivo, frente a cientos de invitados — espectacular, escalable, inolvidable." },
    ],
  },
};

export const BusinessModels = ({ variant }) => {
  const { lang } = useLang();
  const t = (variant === "luxury" ? luxCopy : copy)[lang];
  return (
    <section id="business-models" className="py-24 md:py-40 bg-gradient-to-b from-bg to-[#ece1cd]" data-testid="business-models-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ink leading-[1.05]">{t.h2}</h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl">
          {t.models.map((m, i) => (
            <FadeUp key={m.n} delay={i * 0.1}>
              <div className="h-full bg-surface border border-ink/10 rounded-sm p-8 flex flex-col" data-testid={`model-card-${i + 1}`}>
                <span className="font-serif italic text-5xl text-terracotta/80 leading-none">{m.n}</span>
                <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-ink-soft mt-6 mb-2">{m.tag}</p>
                <h3 className="font-serif text-3xl text-ink mb-4">{m.title}</h3>
                <p className="font-sans text-ink-soft leading-relaxed flex-1">{m.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
